import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  HeaderActionsProvider,
  HeaderActionsSlot,
} from "../context/HeaderActionsContext";
import { TeacherUsage } from "../components/TeacherUsage";
import { ConversationOverview } from "../components/ConversationOverview";
import { AudioRecordingsDashboard } from "../components/AudioRecordingsDashboard";
import { LoadTestDashboard } from "../components/LoadTestDashboard";
import { Analytics } from "../components/analytics/Analytics";
import { FeatureFlagsTab } from "../components/flag-store/FeatureFlagsTab";
import { DataFetch } from "../components/DataFetch";
import { UsersConfig } from "../components/UsersConfig";
import { OrganizationsAdmin } from "../components/OrganizationsAdmin";
import { LessonConfigAdmin } from "../components/LessonConfigAdmin";
import { ClassroomsAdmin } from "../components/ClassroomsAdmin";
import { CurriculumAdmin } from "../components/CurriculumAdmin";
import { PromptList } from "../components/PromptList";
import { ExperimentList } from "../components/ExperimentList";
import { TutorVersionList } from "../components/TutorVersionList";
import { TtsPoolAdmin } from "../components/TtsPoolAdmin";
import { SpeechEvaluation } from "../components/speech-evaluation";
import { CleverLmsAdmin } from "../components/CleverLmsAdmin";
import { SchedulesAdmin } from "../components/schedules/SchedulesAdmin";
import { QuestionBank, QBErrorBoundary } from "../components/question-bank";
import { ClassroomReports } from "../components/reports/ClassroomReports";
import { DashboardSidebar } from "../components/DashboardSidebar";
import { DashboardIam } from "../components/iam/DashboardIam";
import { NoAccess } from "../components/NoAccess";
import {
  ACCESS_CHECK_FAILED_EVENT,
  NO_ACCESS_EVENT,
  type NoAccessDetail,
} from "../lib/access-events";
import { denyBlanksActiveTab } from "../lib/access-state";
import { buildDashboardTabSearchParams } from "../components/lesson-config/url-state";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  TAB_REGISTRY,
  ALL_TAB_KEYS,
  canViewTab,
  getTabDef,
  type TabKey,
} from "../lib/tab-registry";

type Tab = TabKey;

// Shortcuts and valid tabs come from the shared registry.
const TAB_SHORTCUTS: Record<string, Tab> = Object.fromEntries(
  TAB_REGISTRY.map((tab) => [tab.shortcut, tab.id]),
);

const validTabs: Tab[] = ALL_TAB_KEYS;

export const Dashboard = () => {
  const { user, userId, logout, allowedTabs, refreshSession } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // ProtectedRoute waits for the session, so allowedTabs is non-null here.
  const grantedTabs = useMemo(() => allowedTabs ?? [], [allowedTabs]);
  const canView = useCallback(
    (tab: Tab) => canViewTab(grantedTabs, tab),
    [grantedTabs],
  );

  // Tab from the URL, or the first tab the user may view. A direct URL to a
  // tab the user does not hold is kept as-is so the NoAccess screen names it.
  const tabParam = searchParams.get("tab") as Tab | null;
  const firstViewableTab =
    validTabs.find((tab) => canViewTab(grantedTabs, tab)) ?? "usage";
  const resolveTab = useCallback(
    (param: Tab | null): Tab =>
      param && validTabs.includes(param) ? param : firstViewableTab,
    [firstViewableTab],
  );
  const [activeTab, setActiveTab] = useState<Tab>(() => resolveTab(tabParam));
  // Follow later ?tab= changes (browser back/forward, pasted links). State is
  // adjusted during render, the React-endorsed shape for "derive from a prop".
  const [syncedTabParam, setSyncedTabParam] = useState(tabParam);
  if (tabParam !== syncedTabParam) {
    setSyncedTabParam(tabParam);
    setActiveTab(resolveTab(tabParam));
  }

  // A revoke that lands while a tab is open shows up as a 403 on one of its
  // calls; an et-auth outage shows up as a 503. Both replace the tab body.
  const [accessProblem, setAccessProblem] = useState<
    | { kind: "no-grant"; tab: Tab }
    | { kind: "check-failed" }
    | null
  >(null);
  const [rechecking, setRechecking] = useState(false);
  useEffect(() => {
    const onNoAccess = (event: Event) => {
      const detail = (event as CustomEvent<NoAccessDetail>).detail;
      // A tab calls routes that belong to other tabs. A deny on such a
      // subordinate call must not blank a tab the user still holds.
      if (!denyBlanksActiveTab({ tab: detail.tab, tabs: detail.tabs, activeTab })) {
        toast.error("That action needs a tab you do not hold.");
        return;
      }
      const tab = detail.tab && validTabs.includes(detail.tab as Tab) ? (detail.tab as Tab) : activeTab;
      setAccessProblem({ kind: "no-grant", tab });
    };
    const onCheckFailed = () => setAccessProblem({ kind: "check-failed" });
    window.addEventListener(NO_ACCESS_EVENT, onNoAccess);
    window.addEventListener(ACCESS_CHECK_FAILED_EVENT, onCheckFailed);
    return () => {
      window.removeEventListener(NO_ACCESS_EVENT, onNoAccess);
      window.removeEventListener(ACCESS_CHECK_FAILED_EVENT, onCheckFailed);
    };
  }, [activeTab]);
  // Switching tabs clears a stale problem; the new tab makes its own calls.
  const [problemForTab, setProblemForTab] = useState(activeTab);
  if (activeTab !== problemForTab) {
    setProblemForTab(activeTab);
    setAccessProblem(null);
  }
  const recheckAccess = useCallback(async () => {
    setRechecking(true);
    try {
      await refreshSession();
      setAccessProblem(null);
    } catch {
      toast.error("Could not re-check access. Try again in a moment.");
    } finally {
      setRechecking(false);
    }
  }, [refreshSession]);
  const [curriculumDirty, setCurriculumDirty] = useState(false);
  const curriculumId = searchParams.get("curriculumId");
  const curriculumName = searchParams.get("curriculumName");
  const isCurriculumEditor = activeTab === "curriculum" && Boolean(curriculumId);

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (tab: Tab) => {
      if (activeTab === "curriculum" && curriculumDirty && !window.confirm("Discard unsaved curriculum changes?")) return;
      setActiveTab(tab);
      setCurriculumDirty(false);
      setSearchParams((current) => {
        const next = buildDashboardTabSearchParams(current, tab);
        if (tab === "curriculum") {
          next.delete("curriculumId");
          next.delete("curriculumName");
        }
        return next;
      });
    },
    [activeTab, curriculumDirty, setSearchParams],
  );

  const handleCurriculumBack = useCallback(() => {
    if (curriculumDirty && !window.confirm("Discard unsaved curriculum changes?")) return;
    setSearchParams((current) => {
      current.delete("curriculumId");
      current.delete("curriculumName");
      current.set("tab", "curriculum");
      return current;
    });
    setCurriculumDirty(false);
  }, [curriculumDirty, setSearchParams]);

  // Quick navigation shortcuts (digits plus lesson/prompt/tutor keys)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;
      if (isTyping || event.metaKey || event.ctrlKey || event.altKey) return;

      const shortcut = event.key.toLowerCase();
      const tab = TAB_SHORTCUTS[shortcut];
      if (!tab) return;
      if (!canView(tab)) return;

      event.preventDefault();
      handleTabChange(tab);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleTabChange, canView]);

  const getPageTitle = () => getTabDef(activeTab).label;

  return (
    <HeaderActionsProvider>
      <SidebarProvider className="h-svh !min-h-0 overflow-hidden">
        <DashboardSidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          user={user}
          allowedTabs={grantedTabs}
          onLogout={logout}
        />
        <SidebarInset className="flex h-full min-w-0 flex-col overflow-hidden transition-[margin] duration-200 ease-linear">
          <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-3 border-b bg-sidebar px-5 transition-[width,margin] duration-200 ease-linear">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="h-5" />
            {isCurriculumEditor ? (
              <div className="flex items-center gap-3 text-base">
                <button
                  type="button"
                  className="text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  onClick={handleCurriculumBack}
                >
                  Curriculum
                </button>
                <span className="text-muted-foreground" aria-hidden="true">/</span>
                <h1 className="font-semibold text-foreground">
                  {curriculumName || `Curriculum ${curriculumId}`}
                </h1>
              </div>
            ) : (
              <h1 className="text-base font-semibold text-foreground">
                {getPageTitle()}
              </h1>
            )}
            <div className="ml-auto flex items-center gap-2">
              <HeaderActionsSlot />
            </div>
          </header>

          <main className="flex flex-1 min-h-0 flex-col overflow-auto p-4">
            <div
              key={activeTab}
              className="flex h-full min-h-0 flex-col animate-in fade-in duration-200"
            >
              {accessProblem?.kind === "check-failed" ? (
                <NoAccess kind="check-failed" onRetry={recheckAccess} retrying={rechecking} />
              ) : !canView(activeTab) || accessProblem?.kind === "no-grant" ? (
                <NoAccess
                  kind="no-grant"
                  tabLabel={getTabDef(accessProblem?.kind === "no-grant" ? accessProblem.tab : activeTab).label}
                  onRefresh={recheckAccess}
                  refreshing={rechecking}
                />
              ) : activeTab === "usage" ? (
                <TeacherUsage />
              ) : activeTab === "conversations" ? (
                <ConversationOverview />
              ) : activeTab === "recordings" ? (
                <AudioRecordingsDashboard />
              ) : activeTab === "analytics" ? (
                <Analytics />
              ) : activeTab === "loadtest" ? (
                <LoadTestDashboard />
              ) : activeTab === "classroomreports" ? (
                <ClassroomReports />
              ) : activeTab === "classrooms" ? (
                <ClassroomsAdmin />
              ) : activeTab === "curriculum" ? (
                <CurriculumAdmin onDirtyChange={setCurriculumDirty} />
              ) : activeTab === "featureflags" ? (
                <FeatureFlagsTab />
              ) : activeTab === "users" ? (
                <UsersConfig />
              ) : activeTab === "organizations" ? (
                <OrganizationsAdmin />
              ) : activeTab === "lessonconfig" ? (
                <LessonConfigAdmin />
              ) : activeTab === "prompts" ? (
                <PromptList />
              ) : activeTab === "experiments" ? (
                <ExperimentList />
              ) : activeTab === "tutorversions" ? (
                <TutorVersionList />
              ) : activeTab === "ttspool" ? (
                <TtsPoolAdmin />
              ) : activeTab === "ttsarena" || activeTab === "sttarena" ? (
                <SpeechEvaluation activeTab={activeTab} allowedTabs={grantedTabs} onTabChange={handleTabChange} />
              ) : activeTab === "schedules" ? (
                <SchedulesAdmin />
              ) : activeTab === "questionbank" ? (
                <QBErrorBoundary label="Question Bank">
                  <QuestionBank />
                </QBErrorBoundary>
              ) : activeTab === "cleverlms" ? (
                <CleverLmsAdmin />
              ) : activeTab === "datafetch" ? (
                <DataFetch key={userId} ownerId={userId!} />
              ) : activeTab === "iam" ? (
                <DashboardIam />
              ) : null}
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </HeaderActionsProvider>
  );
};
